import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Box, Stack, Typography } from "@mui/material";

import {
  EVOTRUX_BLUE,
  EVOTRUX_BORDER_GREY,
  EVOTRUX_GREEN,
  REVIEW_STAR_GOLD,
} from "constants/index";

import {
  findFactor,
  formatHistoryAcrossNetwork,
  formatHistoryRating,
  formatHistoryWithYou,
  formatPricePoints,
  formatProfileChecks,
} from "./formatQuoteScore";
import {
  getQuoteScoreLevelStyle,
  QUOTE_SCORE_MUTED_STYLE,
} from "./quoteScorePalette";
import { useContainerWidth } from "./useContainerWidth";

import type {
  QuoteScoreAudience,
  QuoteScoreProfileCheck,
  QuoteScoreRating,
} from "./formatQuoteScore";

const BREAKDOWN_MAX_WIDTH = 1040;

/**
 * Below this container width the three evidence groups stack into one column.
 * Measured against the container's own width (see useContainerWidth), not the
 * viewport's, since this always sits inside a column narrower than the page.
 */
const THREE_COLUMN_MIN_WIDTH = 720;

const EVIDENCE_SIZE = "14px";
const EVIDENCE_ICON = 16;

interface AxisHeadingProps {
  name: string;
  level: QuoteScoreLevel | null;
  muted: boolean;
}

function AxisHeading({ name, level, muted }: AxisHeadingProps) {
  const style =
    level != null ? getQuoteScoreLevelStyle(level) : QUOTE_SCORE_MUTED_STYLE;
  return (
    <Stack spacing={0.25} sx={{ mb: 0.75 }}>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "text.secondary",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Typography>
      {level != null && (
        <Stack direction="row" alignItems="center" spacing={0.625}>
          <Box
            aria-hidden="true"
            sx={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              flexShrink: 0,
              backgroundColor: style.color,
            }}
          />
          <Typography
            sx={{
              fontSize: EVIDENCE_SIZE,
              fontWeight: 700,
              lineHeight: 1.4,
              color: muted ? QUOTE_SCORE_MUTED_STYLE.color : style.color,
            }}
          >
            {style.label}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

// Matches a leading quantity so it can be bolded: "210 total loads booked",
// "15% below the median". Lines with no leading number fall through unchanged.
const LEADING_VALUE = /^(\$?\d[\d,]*(?:\.\d+)?%?)(\s[\s\S]*)$/;

interface EvidenceLineProps {
  text: string;
  muted: boolean;
}

function EvidenceLine({ text, muted }: EvidenceLineProps) {
  const bodyColor = muted ? QUOTE_SCORE_MUTED_STYLE.color : "text.secondary";
  const valueColor = muted ? QUOTE_SCORE_MUTED_STYLE.color : "text.primary";
  const match = LEADING_VALUE.exec(text);
  return (
    <Typography
      sx={{ fontSize: EVIDENCE_SIZE, lineHeight: 1.6, color: bodyColor }}
    >
      {match ? (
        <>
          <Box component="span" sx={{ fontWeight: 700, color: valueColor }}>
            {match[1]}
          </Box>
          {match[2]}
        </>
      ) : (
        text
      )}
    </Typography>
  );
}

interface RatingLineProps {
  rating: QuoteScoreRating;
  muted: boolean;
}

function RatingLine({ rating, muted }: RatingLineProps) {
  const bodyColor = muted ? QUOTE_SCORE_MUTED_STYLE.color : "text.secondary";
  const valueColor = muted ? QUOTE_SCORE_MUTED_STYLE.color : "text.primary";
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <StarIcon
          sx={{
            fontSize: EVIDENCE_ICON,
            color: muted ? EVOTRUX_BORDER_GREY : REVIEW_STAR_GOLD,
          }}
        />
        <Typography
          sx={{ fontSize: EVIDENCE_SIZE, fontWeight: 700, color: valueColor }}
        >
          {rating.value.toFixed(2)}
        </Typography>
        <Typography sx={{ fontSize: EVIDENCE_SIZE, color: bodyColor }}>
          ({rating.reviewCount})
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: EVIDENCE_SIZE, color: EVOTRUX_BORDER_GREY }}>
        ·
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <ThumbUpIcon
          sx={{
            fontSize: EVIDENCE_ICON,
            color: muted ? EVOTRUX_BORDER_GREY : EVOTRUX_BLUE,
          }}
        />
        <Typography
          sx={{ fontSize: EVIDENCE_SIZE, fontWeight: 700, color: valueColor }}
        >
          {rating.satisfactionPercent}%
        </Typography>
        <Typography sx={{ fontSize: EVIDENCE_SIZE, color: bodyColor }}>
          ({rating.satisfactionCount})
        </Typography>
      </Stack>
    </Stack>
  );
}

interface ProfileChecklistItemProps {
  check: QuoteScoreProfileCheck;
  muted: boolean;
}

function ProfileChecklistItem({ check, muted }: ProfileChecklistItemProps) {
  let labelColor = EVOTRUX_BORDER_GREY;
  if (muted) {
    labelColor = QUOTE_SCORE_MUTED_STYLE.color;
  } else if (check.ok) {
    labelColor = "text.secondary";
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {check.ok ? (
        <CheckIcon
          sx={{
            fontSize: EVIDENCE_ICON,
            flexShrink: 0,
            color: muted ? EVOTRUX_BORDER_GREY : EVOTRUX_GREEN,
          }}
        />
      ) : (
        <CloseIcon
          sx={{
            fontSize: EVIDENCE_ICON,
            flexShrink: 0,
            color: EVOTRUX_BORDER_GREY,
          }}
        />
      )}
      <Typography
        sx={{ fontSize: EVIDENCE_SIZE, lineHeight: 1.5, color: labelColor }}
      >
        {check.label}
      </Typography>
    </Stack>
  );
}

interface ProfileChecklistProps {
  checks: QuoteScoreProfileCheck[];
  muted: boolean;
}

function ProfileChecklist({ checks, muted }: ProfileChecklistProps) {
  return (
    <Stack spacing={0.25}>
      {checks.map((check) => (
        <ProfileChecklistItem key={check.label} check={check} muted={muted} />
      ))}
    </Stack>
  );
}

interface QuoteScoreBreakdownProps {
  result: QuoteScoreResult | ShipperQuoteScore;
  audience?: QuoteScoreAudience;
  muted?: boolean;
  stacked?: boolean;
}

/**
 * The expanded evidence behind a quote's competitiveness verdict: how the
 * price compares to the other quotes on this load, the carrier's track
 * record, and how complete their profile is (docs/engineering/design/quote_competitiveness_scoring_design.md
 * §14).
 */
export function QuoteScoreBreakdown({
  result,
  audience = "carrier",
  muted = false,
  stacked,
}: QuoteScoreBreakdownProps) {
  const priceFactor = findFactor(result, "price");
  const historyFactor = findFactor(result, "history");
  const profileFactor = findFactor(result, "profile");
  const { ref, width } = useContainerWidth();

  const isStacked =
    stacked ?? (width === undefined || width < THREE_COLUMN_MIN_WIDTH);

  const historyLines = [
    ...formatHistoryWithYou(historyFactor, audience),
    ...formatHistoryAcrossNetwork(historyFactor),
  ];
  const rating = formatHistoryRating(historyFactor);
  const profileChecks = formatProfileChecks(profileFactor, audience);

  return (
    <Box ref={ref} sx={{ width: "100%", minWidth: 0 }}>
      <Stack
        direction={isStacked ? "column" : "row"}
        spacing={isStacked ? 2 : 4}
        alignItems="flex-start"
        sx={{ minWidth: 0, maxWidth: BREAKDOWN_MAX_WIDTH }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <AxisHeading name="Price" level={priceFactor.label} muted={muted} />
          {priceFactor.label === null ? (
            <EvidenceLine
              text="No other quotes on this load yet"
              muted={muted}
            />
          ) : (
            <Stack spacing={0}>
              {formatPricePoints(priceFactor).map((point) => (
                <EvidenceLine key={point} text={point} muted={muted} />
              ))}
            </Stack>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <AxisHeading
            name="History"
            level={historyFactor.label}
            muted={muted}
          />
          <Stack spacing={0}>
            {historyLines.map((point) => (
              <EvidenceLine key={point} text={point} muted={muted} />
            ))}
            {rating && <RatingLine rating={rating} muted={muted} />}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <AxisHeading
            name="Profile"
            level={profileFactor.label}
            muted={muted}
          />
          <ProfileChecklist checks={profileChecks} muted={muted} />
        </Box>
      </Stack>
    </Box>
  );
}
